/**
 * AdminInbox — admins-only "Open threads" inbox showing every message
 * that came through the Contact Admin widget. Threads can be marked
 * read, closed, or reopened. Each thread shows the sender, subject,
 * message body, and the page URL the sender was on.
 *
 * Route: /admin/inbox — enforced client-side by requiring `user.is_admin`
 * (backend enforces via /api/admins/me/inbox 403 for non-admins).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Inbox as InboxIcon, CheckCircle2, RotateCcw, ExternalLink } from "lucide-react";

import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminInbox() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("open"); // "open" | "closed" | "all"
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState({ open_count: 0, unread_count: 0 });

  const isAdmin = !!(user && (user.is_admin || user.is_super_admin ||
    ["admin", "super_admin", "editor", "organization_admin"].includes(user.platform_role)));

  const load = async () => {
    setBusy(true);
    try {
      const params = tab === "all" ? "" : `?status=${tab}`;
      const { data } = await http.get(`/admins/me/inbox${params}`);
      setRows(Array.isArray(data?.items) ? data.items : []);
      setCounts({ open_count: data?.open_count ?? 0, unread_count: data?.unread_count ?? 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load your inbox.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-line */ }, [tab, isAdmin]);

  const markRead = async (id) => {
    try {
      await http.post(`/admins/me/inbox/${id}/read`);
      setRows((prev) => prev.map((r) => r.id === id ? { ...r, read: true } : r));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark as read.");
    }
  };
  const close = async (id) => {
    try {
      await http.post(`/admins/me/inbox/${id}/close`);
      toast.success("Thread closed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't close thread.");
    }
  };
  const reopen = async (id) => {
    try {
      await http.post(`/admins/me/inbox/${id}/reopen`);
      toast.success("Thread reopened.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reopen thread.");
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6 text-center text-sm" data-testid="admin-inbox-loading">
        Checking your access…
      </div>
    );
  }
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6 text-center" data-testid="admin-inbox-gate">
        <div className="text-sm">Please sign in to see your admin inbox.</div>
        <Link to="/admin/memorials/quick" className="wofy-btn-primary inline-block mt-4">Admin sign in</Link>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6 text-center" data-testid="admin-inbox-gate">
        <div className="text-sm">This inbox is for admins only.</div>
        <Link to="/" className="wofy-btn-primary inline-block mt-4">Back home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 md:px-6" data-testid="admin-inbox">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold flex items-center gap-2" style={{ color: "var(--wofy-primary)" }}>
            <InboxIcon className="w-6 h-6" /> Contact Admin — Open threads
          </h1>
          <div className="text-xs opacity-70 mt-1">
            Every message sent via the "Contact admin" widget on any page.{" "}
            <strong>{counts.open_count} open</strong> · <strong>{counts.unread_count} unread</strong>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs underline opacity-70 hover:opacity-100"
          data-testid="admin-inbox-refresh"
          disabled={busy}
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="flex gap-1 mb-4 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        {[
          { k: "open", label: "Open" },
          { k: "closed", label: "Closed" },
          { k: "all", label: "All" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            data-testid={`admin-inbox-tab-${t.k}`}
            className="px-4 py-2 text-sm border-b-2 -mb-px"
            style={{
              borderColor: tab === t.k ? "var(--wofy-primary)" : "transparent",
              color: tab === t.k ? "var(--wofy-primary)" : "inherit",
              fontWeight: tab === t.k ? 600 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 opacity-70 text-sm" data-testid="admin-inbox-empty">
          No {tab === "all" ? "" : tab} threads yet.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-inbox-list">
          {rows.map((r) => (
            <ThreadCard
              key={r.id}
              t={r}
              onOpen={() => !r.read && markRead(r.id)}
              onClose={() => close(r.id)}
              onReopen={() => reopen(r.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ThreadCard({ t, onOpen, onClose, onReopen }) {
  const [expanded, setExpanded] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replies, setReplies] = useState(t.replies || []);
  const when = t.created_at ? new Date(t.created_at).toLocaleString() : "";
  const preview = (t.message || "").slice(0, 180);
  const canReply = !!(t.from_email && t.from_email.includes("@"));

  const submitReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await http.post(`/admins/me/inbox/${t.id}/reply`, { message: reply.trim() });
      if (data?.ok) {
        toast.success(`Reply sent to ${t.from_email}.`);
      } else {
        toast.error(data?.error || "Delivery failed — reply is logged for reference.");
      }
      setReplies((prev) => [...prev, data?.reply]);
      setReply("");
      setReplyOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <li
      className="rounded-xl border p-4 bg-white"
      style={{
        borderColor: t.read ? "var(--wofy-border)" : "var(--wofy-primary)",
        borderLeftWidth: 3,
        boxShadow: t.read ? undefined : "0 1px 3px rgba(178,93,56,0.08)",
      }}
      data-testid={`admin-inbox-thread-${t.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {!t.read && (
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: "var(--wofy-primary)" }}
                aria-label="unread"
              />
            )}
            <span
              className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{
                background: t.status === "closed" ? "rgba(0,0,0,0.06)" : "rgba(194,65,12,0.10)",
                color: t.status === "closed" ? "#666" : "var(--wofy-primary)",
              }}
            >
              {t.status}
            </span>
            <span className="text-[10px] opacity-60">{when}</span>
          </div>
          <div className="text-sm font-semibold truncate" title={t.subject}>
            {t.subject || "(no subject)"}
          </div>
          <div className="text-xs opacity-80 mt-0.5 flex items-center gap-1 flex-wrap">
            <Mail className="w-3 h-3" />
            <span>from <strong>{t.from_name || "Anonymous"}</strong>{t.from_email ? ` <${t.from_email}>` : ""}</span>
            {t.page_url && (
              <a
                href={t.page_url}
                target="_blank"
                rel="noreferrer"
                className="underline opacity-70 hover:opacity-100 inline-flex items-center gap-0.5 ml-1"
              >
                on page <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {t.status === "open" ? (
            <button
              type="button"
              onClick={onClose}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-emerald-50"
              style={{ borderColor: "var(--wofy-border)", color: "#0f766e" }}
              data-testid={`admin-inbox-close-${t.id}`}
              title="Mark thread as closed"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Close
            </button>
          ) : (
            <button
              type="button"
              onClick={onReopen}
              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-orange-50"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-primary)" }}
              data-testid={`admin-inbox-reopen-${t.id}`}
              title="Reopen thread"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reopen
            </button>
          )}
        </div>
      </div>
      <div
        className="mt-2 text-sm whitespace-pre-wrap"
        style={{ color: "var(--wofy-ink)" }}
        onClick={() => { setExpanded((v) => !v); onOpen?.(); }}
        role="button"
        tabIndex={0}
        data-testid={`admin-inbox-body-${t.id}`}
      >
        {expanded ? (t.message || "") : (preview + (t.message && t.message.length > 180 ? "…" : ""))}
        {t.message && t.message.length > 180 && (
          <button type="button" className="ml-2 text-xs underline opacity-70" onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
            {expanded ? "less" : "more"}
          </button>
        )}
      </div>

      {/* Previous replies from admins (compact log). */}
      {replies.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`admin-inbox-replies-${t.id}`}>
          {replies.map((r, i) => (
            <li
              key={i}
              className="rounded-md px-3 py-2 text-xs"
              style={{ background: "rgba(178,93,56,0.06)", borderLeft: "2px solid var(--wofy-primary)" }}
            >
              <div className="opacity-70 mb-0.5">
                <strong>{r?.from_name || "You"}</strong> replied
                {r?.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ""}
                {r?.sent_ok === false && <span className="text-red-600 ml-1">(delivery failed)</span>}
              </div>
              <div className="whitespace-pre-wrap">{r?.message}</div>
            </li>
          ))}
        </ul>
      )}

      {/* Reply composer — direct in-app reply. Uses the admin's real
          email as reply_to so the sender's next reply lands in the
          admin's personal inbox. */}
      {canReply && (
        <div className="mt-3">
          {!replyOpen ? (
            <button
              type="button"
              onClick={() => setReplyOpen(true)}
              className="text-xs underline"
              style={{ color: "var(--wofy-primary)" }}
              data-testid={`admin-inbox-reply-open-${t.id}`}
            >
              Reply to {t.from_name || t.from_email}
            </button>
          ) : (
            <div className="rounded-lg border p-2 space-y-2" style={{ borderColor: "var(--wofy-border)" }}>
              <textarea
                rows={3}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={`Reply to ${t.from_name || t.from_email}…`}
                className="w-full px-2 py-1.5 rounded text-sm outline-none"
                style={{ background: "transparent" }}
                data-testid={`admin-inbox-reply-input-${t.id}`}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setReplyOpen(false); setReply(""); }}
                  className="text-xs px-3 py-1 rounded opacity-70 hover:opacity-100"
                  data-testid={`admin-inbox-reply-cancel-${t.id}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReply}
                  disabled={!reply.trim() || sending}
                  className="text-xs px-3 py-1 rounded text-white"
                  style={{ background: reply.trim() ? "var(--wofy-primary)" : "#D6C7A4" }}
                  data-testid={`admin-inbox-reply-send-${t.id}`}
                >
                  {sending ? "Sending…" : "Send reply"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {!canReply && (
        <div className="mt-2 text-[11px] opacity-60" data-testid={`admin-inbox-no-reply-${t.id}`}>
          No reply-to email — sender was anonymous.
        </div>
      )}
    </li>
  );
}
